<template>
    <div>
        <input-text
            :disabled="disabled"
            name="name"
            label="* H1 Title"
            v-model="form.name"
            :error="errors.name ? errors.name[0] : null"
            v-if="!useRichText"
            :maxlength="200"
        >
            <p class="my-1 text-xs text-gray-600 dark:text-gray-200">You can use these available shortcodes in this H1 title field: [current_year], [current_month] and [current_abrev_month]</p>
        </input-text>

        <input-rich-text
            :disabled="disabled"
            name="name"
            label="* H1 Title"
            v-model="form.name"
            :error="errors.name ? errors.name[0] : null"
            v-if="useRichText"
            :toolbarButtons="['italic']"
            :maxlength="255"
        >
            <p v-if="h1AnchorContainsShortcode" class="my-1 text-xs text-gray-600 dark:text-gray-200">You can use these available shortcodes in this H1 title field: [current_year], [current_month] and [current_abrev_month]</p>
        </input-rich-text>

        <input-text
            :disabled="disabled"
            name="h1_anchor"
            label="Jump Id for H1 (excluding #)"
            v-model="form.h1_anchor"
            :error="errors.h1_anchor ? errors.h1_anchor[0] : null"
            :maxlength="255"
            class="mt-3"
        >
            <p v-if="h1AnchorContainsShortcode" class="my-1 text-xs text-gray-600 dark:text-gray-200">You can use these available shortcodes in this H1 title field: [current_year], [current_month] and [current_abrev_month]</p>
        </input-text>

        <input-text
            name="slug"
            label="* Slug"
            v-if="!hideSlug"
            v-model="form.slug"
            :disabled="disableSlug || disabled"
            class="mt-3"
            :error="errors.slug ? errors.slug[0] : null"
        />

        <input-text
            :disabled="disabled"
            name="breadcrumb"
            label="* Breadcrumb"
            v-if="!hideBreadcrumb"
            v-model="form.breadcrumb"
            class="mt-3"
            :error="errors.breadcrumb ? errors.breadcrumb[0] : null"
            :maxlength="255"
        />

        <input-checkbox
            v-if="replaceBreadcrumbEnabled"
            @change="() => form.regenerate_breadcrumbs = true"
            name="replace_breadcrumb"
            label="Replace breadcrumb with home page (this page's breadcrumb will be replaced on any child pages)"
            class="mt-3"
            v-model="form.replace_breadcrumb"
            :error="errors.replace_breadcrumb ? errors.replace_breadcrumb[0] : null"
        />

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
            v-if="!hideParent"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Parent</p>
            <polymorphic-reference
                :disabled="disabled"
                :creatable="false"
                :should-open-new-window="true"
                :deletable="parentLinkEnabled"
                :linkable="parentLinkEnabled"
                :language="form.language"
                v-model="form.parent"
                :models="filteredLinkableTypes"
                :query="{
                    status_id: 1,
                }"
                :error="errors.parent_id ? errors.parent_id[0] : null"
            />
        </div>

        <input-text
            label="URI"
            name="uri"
            :disabled="true"
            v-if="!hideUri"
            v-model="form.uri"
            class="mt-3"
            :error="errors.uri ? errors.uri[0] : null"
        />

        <input-date
            :disabled="true"
            label="Created At"
            name="created_at"
            v-model="form.created_at"
            :clear-button="false"
            class="mt-3"
            :error="errors.created_at ? errors.created_at[0] : null"
        />

        <input-date
            v-show="editableTimestamps"
            :disabled="disabled"
            label="Created At Override"
            name="created_at_override"
            v-model="form.created_at_override"
            class="mt-3"
            :error="errors.created_at_override ? errors.created_at_override[0] : null"
        />

        <input-date
            :disabled="true"
            label="Updated At"
            name="updated_at"
            v-model="form.updated_at"
            :clear-button="false"
            class="mt-3"
            :error="errors.updated_at ? errors.updated_at[0] : null"
        />

        <input-date
            v-show="editableTimestamps"
            :disabled="disabled"
            label="Updated At Override"
            name="updated_at_override"
            v-model="form.updated_at_override"
            class="mt-3"
            :error="errors.updated_at_override ? errors.updated_at_override[0] : null"
        />

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Published/Draft</p>
            <reference
                create-text="Create new status and link status to this page"
                link-text="Link an existing status"
                :disabled="disabled"
                v-model="form.status"
                model-data="Status"
                :create-should-have-confirmation="statusCreateShouldHaveConfirmation"
                confirmation-message="Are you sure you want to publish your page? You won’t be allowed to edit slugs."
                :creatable="false"
                :excluded-ids="excludedStatusIds"
                :error="errors.status_id ? errors.status_id[0] : null"
            />
        </div>

        <div
            class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 dark:text-gray-200 mb-4">Meta title, description and featured image</p>
            <reference
                create-text="Create new meta title, description and featured image and link them to this page"
                link-text="Link an existing meta title, description and featured image"
                :disabled="disabled"
                v-model="form.meta"
                model-data="Meta"
                :error="errors.meta_id ? errors.meta_id[0] : null"
                :linkable="false"
                :editable="true"
                :language="form.language"
            />
        </div>

        <div v-if="$hasRole('Admin')">
            <href-lang
                :variants="form.variants"
                :disabled="disabled"
                v-if="!hideHrefLangs"
                v-model="form.href_langs"
                :errors="errors"
            >
            </href-lang>
        </div>

    </div>
</template>

<script>
export default {
    name: "BasePageFields",
    props: {
        form: {
            type: Object,
            required: true,
        },
        errors: {
            type: [Array, Object],
            required: true,
        },
        editableTimestamps: {
            type: Boolean,
            required: false,
            default: false,
        },
        hideSlug: {
            type: Boolean,
            required: false,
        },
        hideBreadcrumb: {
            type: Boolean,
            required: false,
        },
        hideParent: {
            type: Boolean,
            required: false,
        },
        hideUri: {
            type: Boolean,
            required: false,
        },
        hideHrefLangs: {
            type: Boolean,
            required: false,
        },
        disabled: {
            type: Boolean,
            default: false
        },
        useRichText: {
            type: Boolean,
            default: false,
        },
        h1AnchorContainsShortcode: {
            type: Boolean,
            default: false,
        },
        publishable: {
            type: Boolean,
            required: false,
            default: true,
        },
        replaceBreadcrumbEnabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        linkableTypes: {
            type: Array,
            default: () => [
                "Category",
                "Software Category",
                "Payment Category",
                "License Category",
                "Country Category",
                "Bonus Type Category",
                "Game Type Category",
                "Casino Review",
                "Slot Review",
                "Game Review",
                "Author",
                "About Us Page",
                "Payment Method Main Page",
                "License Main Page",
                "Knowledge Hub Page",
                "Blog Main Page",
                "Guide Main Page",
                "Home Page",
                "Html Sitemap"
            ],
        },
        excludedTypes: {
            type: Array,
            default: () => [],
        },
    },
    data(){
        return {
            statuses: this.getStatuses(),
        }
    },
    computed: {
        filteredLinkableTypes() {
            return this.linkableTypes.filter(type => this.excludedTypes.indexOf(type) < 0);
        },
        disableSlug () {
            return this.form.status && this.form.status.name == 'Published';
        },
        parentLinkEnabled () {
            return this.form.status && this.form.status.name != 'Published';
        },
        excludedStatusIds () {
            return this.publishable ? [] : ['1'];
        },
    },
    methods: {
        getStatuses: function(){
            axios.get(route('api.statuses.index'))
            .then(response => {
                this.statuses = response.data.data;
            })
            .catch(error => {
                this.$notify({
                    group: "formError",
                    title: "Error Fetching Statuses 2",
                    text: route('api.statuses.index') + '. ' + JSON.stringify(error),
                });
            })
        },
        statusCreateShouldHaveConfirmation (status) {
            // only have confirmation dialog if changing to published
            return status.name === 'Published';
        },
    },
}
</script>
